import React, { useRef, useState, useEffect } from 'react';
import AuthModal from '../components/AuthModal';
import ScanPreviewModal from '../components/ScanPreviewModal';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Leaf, Droplets, Heart, Sparkles, ArrowRight, Camera, LineChart, Zap, Download, Smartphone, Check, MessageCircle, Star, Shield, ChevronDown, Users, TrendingUp, ScanFace, Play } from 'lucide-react';
import EmailCapture from '../components/EmailCapture';
import HowItWorksDemo from '../components/HowItWorksDemo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FEATURES = [
  { id: "track", icon: <Heart className="w-6 h-6" />, title: "Track Your Skin & Body", description: "Log acne, bloating, food, and cycle days to uncover hidden patterns" },
  { id: "analysis", icon: <LineChart className="w-6 h-6" />, title: "AI Pattern Analysis", description: "Discover triggers like 'Dairy causes breakouts' using your own data" },
  { id: "detox", icon: <Zap className="w-6 h-6" />, title: "Daily Detox Score", description: "Gamified wellness tracking for hydration, gut health & inflammation" },
  { id: "progress", icon: <Camera className="w-6 h-6" />, title: "Skin Progress AI", description: "Upload selfies and track your skin improvement over time with AI" },
];

const STATS = [
  { value: "10K+", label: "Daily Logs Tracked" },
  { value: "85%", label: "Users See Improvement" },
  { value: "4.8", label: "User Rating", icon: <Star className="w-4 h-4 fill-current" /> },
  { value: "30+", label: "Holistic Supplements" },
];

const TESTIMONIALS = [
  { name: "Alex R.", avatar: "A", text: "I finally figured out that dairy was causing my chin breakouts. My skin has been clear for 3 weeks now!", rating: 5, tag: "Discovered food trigger" },
  { name: "Sam K.", avatar: "S", text: "The detox score is addicting! I've been on a 21-day streak and my skin has never looked better.", rating: 5, tag: "21-day streak" },
  { name: "Jordan M.", avatar: "J", text: "The AI Skin Coach told me my stress levels were spiking my acne. Started meditation and saw results in days.", rating: 5, tag: "AI Coach insight" },
  { name: "Taylor P.", avatar: "T", text: "Love the before/after selfie comparison. Actually seeing my progress keeps me motivated to stay consistent.", rating: 4, tag: "Visual progress" },
];

const FAQ_ITEMS = [
  { q: "Is InnerGlow free?", a: "Yes! Core features like daily logging, detox scoring, streak tracking, and AI Skin Coach are completely free. Premium unlocks personalized product recommendations and advanced analysis." },
  { q: "How does the AI know what's causing my acne?", a: "InnerGlow analyzes YOUR data — the foods you eat, stress levels, sleep, and hydration — to find correlations. For example, if your acne spikes 2 days after eating dairy, the AI flags it as a potential trigger." },
  { q: "Is this app for all skin types and genders?", a: "Absolutely! InnerGlow is designed to be gender-neutral and works for all skin types. Hormone cycle tracking is optional — you choose whether to enable it during onboarding." },
  { q: "Do I need to upload selfies?", a: "Selfie uploads are optional. They enable the AI Skin Progress feature which tracks your acne improvement visually over time. Your photos are private and never shared." },
  { q: "Can I use this on my phone?", a: "Yes! InnerGlow is a Progressive Web App. Open it in your browser, tap 'Add to Home Screen', and it works like a native app — full screen, push notifications, and offline support." },
  { q: "What makes this different from other skincare apps?", a: "InnerGlow connects the dots between your lifestyle and skin. Instead of generic advice, you get insights based on YOUR actual data — your food diary, stress patterns, sleep quality, and hormone cycles." },
];

const PLAN_FEATURES = [
  { feature: "Daily Logging & Detox Score", free: true, premium: true },
  { feature: "Streak Tracking & Badges", free: true, premium: true },
  { feature: "AI Skin Coach Chat", free: true, premium: true },
  { feature: "Food Trigger Heatmap", free: true, premium: true },
  { feature: "Skin Progress AI (Selfies)", free: true, premium: true },
  { feature: "Pattern Detection", free: true, premium: true },
  { feature: "Supplement Advice & Routines", free: true, premium: true },
  { feature: "Personalized Product Recommendations", free: false, premium: true },
  { feature: "Advanced Inflammation Analysis", free: false, premium: true },
  { feature: "Priority AI Analysis", free: false, premium: true },
];

const HeroSection = ({ onLogin, onLearnMore, onInstantScan, scanLoading, onWatchDemo }) => (
  <div className="relative overflow-hidden">
    <div className="absolute inset-0 opacity-30">
      <img src="https://images.pexels.com/photos/8516701/pexels-photo-8516701.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="" className="w-full h-full object-cover" />
    </div>
    <div className="relative max-w-7xl mx-auto px-6 py-20 lg:py-28">
      <div className="max-w-2xl">
        <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#4ADE80]/20 text-[#FF6B4A] text-sm font-medium mb-6">
          <Leaf className="w-4 h-4" /> Holistic Skin Wellness
        </span>
        <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-[#1A1514] tracking-tight leading-tight mb-4">InnerGlow</h1>
        <p className="text-base sm:text-lg text-[#8C7A76] leading-relaxed mb-4 max-w-xl">Your personal skin + hormone tracker. Log daily, discover patterns, and get AI-powered recommendations for clearer, healthier skin.</p>

        {/* Instant paid scan highlight */}
        <div className="mb-8 p-5 rounded-2xl bg-gradient-to-br from-[#FF6B4A] to-[#E8552F] text-white max-w-lg shadow-lg">
          <div className="flex items-center gap-2 mb-1">
            <ScanFace className="w-5 h-5 text-[#4ADE80]" />
            <span className="font-heading text-lg font-bold">Instant AI Skin Analysis</span>
          </div>
          <p className="text-sm text-white/75 mb-4">Get a full 30-point clinical skin report, mapped on your photo — no signup needed.</p>
          <Button onClick={onInstantScan} disabled={scanLoading} data-testid="instant-scan-btn" className="w-full sm:w-auto bg-[#4ADE80] hover:bg-[#94a37b] text-[#161b17] rounded-full px-7 py-6 text-base font-bold transition-all duration-300 hover:-translate-y-0.5">
            <Camera className="mr-2 w-5 h-5" /> {scanLoading ? 'Starting…' : 'Scan My Skin — $1.99'}
          </Button>
        </div>

        <div className="flex flex-wrap gap-4 mb-10">
          <Button onClick={onLogin} data-testid="get-started-btn" className="bg-[#FF6B4A] hover:bg-[#E8552F] text-white rounded-full px-8 py-6 text-lg font-semibold transition-all duration-300 hover:-translate-y-1">
            Get Started Free <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
          <Button variant="outline" data-testid="learn-more-btn" onClick={onLearnMore} className="border-[#E5D0C9] hover:bg-[#E5D0C9]/50 rounded-full px-8 py-6 text-lg text-[#1A1514]">Learn More</Button>
          <button onClick={onWatchDemo} data-testid="watch-demo-btn" className="inline-flex items-center gap-2 text-lg font-semibold text-[#FF6B4A] hover:text-[#E8552F] transition-colors px-2">
            <span className="w-9 h-9 rounded-full bg-[#FF6B4A] text-white flex items-center justify-center"><Play className="w-4 h-4 ml-0.5 fill-white" /></span>
            Watch demo
          </button>
        </div>
        {/* Trust bar */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {STATS.map((stat) => (
            <div key={stat.label} className="text-center">
              <p className="font-heading text-2xl font-bold text-[#1A1514] flex items-center justify-center gap-1">
                {stat.icon && stat.icon}{stat.value}
              </p>
              <p className="text-xs text-[#8C7A76] mt-1">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

const FeaturesGrid = () => (
  <div id="features" className="max-w-7xl mx-auto px-6 py-20">
    <div className="text-center mb-16">
      <span className="overline text-[#8C7A76]">How It Works</span>
      <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#1A1514] mt-4">Your Skin Wellness Journey</h2>
      <p className="text-[#8C7A76] mt-3 max-w-lg mx-auto">Four simple steps to understand and transform your skin health</p>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      {FEATURES.map((feature, index) => (
        <div key={feature.id} className="card-elevated p-8 animate-fade-in relative" style={{ animationDelay: `${index * 0.1}s` }}>
          <div className="absolute top-4 right-4 text-[#E5D0C9] font-heading text-4xl font-bold">{index + 1}</div>
          <div className="w-12 h-12 rounded-full bg-[#4ADE80]/20 flex items-center justify-center text-[#FF6B4A] mb-4">{feature.icon}</div>
          <h3 className="font-heading text-lg font-semibold text-[#1A1514] mb-2">{feature.title}</h3>
          <p className="text-[#8C7A76] text-sm leading-relaxed">{feature.description}</p>
        </div>
      ))}
    </div>
  </div>
);

const AICoachPreview = ({ onLogin }) => (
  <div className="bg-white py-20">
    <div className="max-w-7xl mx-auto px-6">
      <div className="grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#FF6B4A]/10 text-[#FF6B4A] text-sm font-medium mb-6">
            <Sparkles className="w-4 h-4" /> AI-Powered
          </span>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#1A1514] mb-6">Your Personal AI Skin Coach</h2>
          <p className="text-[#8C7A76] text-lg leading-relaxed mb-6">Ask anything about your skin. The AI uses YOUR logged data to give personalized advice — not generic tips from the internet.</p>
          <div className="space-y-4 mb-8">
            {[
              "\"Why am I breaking out this week?\"",
              "\"What foods should I avoid?\"",
              "\"Analyze my skin trends\"",
            ].map((q) => (
              <div key={q} className="flex items-center gap-3">
                <MessageCircle className="w-4 h-4 text-[#FF6B4A] flex-shrink-0" />
                <p className="text-[#1A1514] text-sm italic">{q}</p>
              </div>
            ))}
          </div>
          <Button onClick={onLogin} className="bg-[#FF6B4A] hover:bg-[#E8552F] text-white rounded-full px-6 py-5 font-semibold transition-all hover:-translate-y-1" data-testid="coach-cta">
            Try AI Coach Free <Sparkles className="ml-2 w-4 h-4" />
          </Button>
        </div>
        <div className="flex justify-center">
          {/* Chat mockup */}
          <div className="w-80 bg-white rounded-2xl shadow-xl border border-[#E5D0C9] overflow-hidden">
            <div className="bg-[#FF6B4A] px-4 py-3 flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-white/15 flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-white" />
              </div>
              <div>
                <p className="text-white font-bold text-sm">AI Skin Coach</p>
                <p className="text-white/60 text-xs">Powered by your data</p>
              </div>
            </div>
            <div className="p-4 space-y-3">
              <div className="flex justify-end">
                <div className="bg-[#FF6B4A] text-white px-4 py-2 rounded-2xl rounded-br-md text-sm max-w-[80%]">Why am I breaking out this week?</div>
              </div>
              <div className="flex justify-start gap-2">
                <div className="w-6 h-6 rounded-full bg-[#FF6B4A] flex items-center justify-center flex-shrink-0 mt-1">
                  <Sparkles className="w-3 h-3 text-white" />
                </div>
                <div className="bg-[#F4F2EE] px-4 py-2 rounded-2xl rounded-bl-md text-sm text-[#1A1514] max-w-[80%]">
                  Based on your logs, your acne spiked after 3 consecutive days of dairy. Your stress was also 7/10 yesterday. Try cutting dairy for 3 days and see if it helps!
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const SocialProof = () => (
  <div className="bg-[#FFF2EC] py-20">
    <div className="max-w-7xl mx-auto px-6">
      <div className="text-center mb-12">
        <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#4ADE80]/15 text-[#FF6B4A] text-sm font-medium mb-4">
          <Users className="w-4 h-4" /> Real Results
        </span>
        <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#1A1514]">What Our Users Say</h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {TESTIMONIALS.map((t) => (
          <div key={t.name} className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center gap-1 mb-3">
              {Array.from({ length: t.rating }).map((_, i) => (
                <Star key={i} className="w-4 h-4 text-[#A388EE] fill-current" />
              ))}
            </div>
            <p className="text-sm text-[#1A1514] leading-relaxed mb-4">"{t.text}"</p>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-[#FF6B4A] flex items-center justify-center text-white text-xs font-bold">{t.avatar}</div>
                <p className="text-sm font-semibold text-[#1A1514]">{t.name}</p>
              </div>
              <span className="text-[10px] text-[#FF6B4A] bg-[#4ADE80]/15 px-2 py-1 rounded-full font-medium">{t.tag}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

const DetoxPreview = () => (
  <div className="bg-[#FF6B4A] py-20">
    <div className="max-w-7xl mx-auto px-6">
      <div className="grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 text-white/80 text-sm font-medium mb-6">
            <Sparkles className="w-4 h-4" /> Daily Gamification
          </span>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-6">Your Daily Detox Score</h2>
          <p className="text-white/70 text-lg leading-relaxed mb-8">Track hydration, gut health, and inflammation. Build streaks, earn badges, and watch your score improve daily.</p>
          <div className="flex gap-8">
            <div className="text-center"><Droplets className="w-8 h-8 text-[#4ADE80] mx-auto mb-2" /><p className="text-white font-semibold">Hydration</p></div>
            <div className="text-center"><Heart className="w-8 h-8 text-[#A388EE] mx-auto mb-2" /><p className="text-white font-semibold">Gut Health</p></div>
            <div className="text-center"><Zap className="w-8 h-8 text-[#4ADE80] mx-auto mb-2" /><p className="text-white font-semibold">Inflammation</p></div>
          </div>
        </div>
        <div className="flex justify-center">
          <div className="relative">
            <div className="w-48 h-48 rounded-full bg-gradient-to-br from-[#4ADE80] to-[#FF6B4A] flex items-center justify-center shadow-2xl">
              <div className="text-center">
                <span className="font-heading text-6xl font-bold text-white">85</span>
                <p className="text-white/80 text-sm mt-1">out of 100</p>
              </div>
            </div>
            <div className="absolute -top-4 -right-4 w-16 h-16 rounded-full bg-[#A388EE] flex items-center justify-center text-white font-semibold text-sm">
              <TrendingUp className="w-4 h-4 mr-0.5" />5%
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const PricingComparison = ({ onLogin }) => (
  <div className="max-w-7xl mx-auto px-6 py-20">
    <div className="text-center mb-12">
      <span className="overline text-[#A388EE]">Pricing</span>
      <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#1A1514] mt-4">Free vs Premium</h2>
      <p className="text-[#8C7A76] mt-3">Most features are free forever. Premium unlocks the full experience.</p>
    </div>
    <div className="max-w-2xl mx-auto">
      <div className="bg-white rounded-2xl shadow-sm border border-[#E5D0C9] overflow-hidden">
        <div className="grid grid-cols-3 bg-[#FFF2EC] border-b border-[#E5D0C9]">
          <div className="p-4"><p className="text-sm font-semibold text-[#1A1514]">Feature</p></div>
          <div className="p-4 text-center border-x border-[#E5D0C9]">
            <p className="text-sm font-semibold text-[#1A1514]">Free</p>
            <p className="text-xs text-[#8C7A76]">$0 forever</p>
          </div>
          <div className="p-4 text-center bg-[#FF6B4A]/5">
            <p className="text-sm font-semibold text-[#FF6B4A]">Premium</p>
            <p className="text-xs text-[#8C7A76]">$10/month</p>
          </div>
        </div>
        {PLAN_FEATURES.map((row) => (
          <div key={row.feature} className="grid grid-cols-3 border-b border-[#E5D0C9] last:border-0">
            <div className="p-3 flex items-center">
              <p className="text-sm text-[#1A1514]">{row.feature}</p>
            </div>
            <div className="p-3 flex items-center justify-center border-x border-[#E5D0C9]">
              {row.free ? <Check className="w-5 h-5 text-[#4ADE80]" /> : <span className="text-[#E5D0C9]">—</span>}
            </div>
            <div className="p-3 flex items-center justify-center bg-[#FF6B4A]/5">
              <Check className="w-5 h-5 text-[#FF6B4A]" />
            </div>
          </div>
        ))}
      </div>
      <div className="text-center mt-8">
        <Button onClick={onLogin} className="bg-[#FF6B4A] hover:bg-[#E8552F] text-white rounded-full px-8 py-5 text-base font-semibold transition-all hover:-translate-y-1" data-testid="pricing-cta">
          Start Free — Upgrade Anytime <ArrowRight className="ml-2 w-5 h-5" />
        </Button>
      </div>
    </div>
  </div>
);

const FAQSection = () => {
  const [openIndex, setOpenIndex] = React.useState(null);
  return (
    <div id="faq" className="bg-[#FFF2EC] py-20">
      <div className="max-w-3xl mx-auto px-6">
        <div className="text-center mb-12">
          <span className="overline text-[#8C7A76]">FAQ</span>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#1A1514] mt-4">Common Questions</h2>
        </div>
        <div className="space-y-3">
          {FAQ_ITEMS.map((item, i) => (
            <div key={i} className="bg-white rounded-xl overflow-hidden shadow-sm">
              <button
                onClick={() => setOpenIndex(openIndex === i ? null : i)}
                className="w-full px-6 py-4 flex items-center justify-between text-left"
                data-testid={`faq-${i}`}
              >
                <span className="font-semibold text-[#1A1514] text-sm pr-4">{item.q}</span>
                <ChevronDown className={`w-5 h-5 text-[#8C7A76] flex-shrink-0 transition-transform duration-200 ${openIndex === i ? 'rotate-180' : ''}`} />
              </button>
              {openIndex === i && (
                <div className="px-6 pb-4">
                  <p className="text-sm text-[#8C7A76] leading-relaxed">{item.a}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const GetTheApp = ({ onLogin }) => (
  <div id="download" className="bg-[#F4F2EE] py-20">
    <div className="max-w-7xl mx-auto px-6">
      <div className="grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#FF6B4A]/10 text-[#FF6B4A] text-sm font-medium mb-6">
            <Smartphone className="w-4 h-4" /> Available on All Devices
          </span>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#1A1514] mb-6">Get InnerGlow on Your Phone</h2>
          <p className="text-[#8C7A76] text-lg leading-relaxed mb-8">Install InnerGlow directly from your browser — no App Store needed. Works on iPhone, Android, and desktop.</p>
          <div className="space-y-4 mb-8">
            {["Full-screen app experience", "Push notifications for streaks", "Works offline", "Free to install"].map((item) => (
              <div key={item} className="flex items-center gap-3">
                <div className="w-6 h-6 rounded-full bg-[#4ADE80]/20 flex items-center justify-center flex-shrink-0">
                  <Check className="w-3.5 h-3.5 text-[#FF6B4A]" />
                </div>
                <p className="text-[#1A1514] text-sm font-medium">{item}</p>
              </div>
            ))}
          </div>
          <Button onClick={onLogin} data-testid="download-cta-btn" className="bg-[#FF6B4A] hover:bg-[#E8552F] text-white rounded-full px-8 py-5 text-base font-semibold transition-all hover:-translate-y-1">
            <Download className="mr-2 w-5 h-5" /> Install InnerGlow
          </Button>
        </div>
        <div className="flex justify-center">
          <div className="w-64 h-[480px] bg-[#1A1514] rounded-[40px] p-3 shadow-2xl">
            <div className="w-full h-full bg-[#FFF2EC] rounded-[32px] overflow-hidden flex flex-col">
              <div className="bg-[#FF6B4A] px-5 pt-8 pb-4">
                <p className="text-white/60 text-xs">Welcome back</p>
                <p className="text-white font-bold text-lg mt-1">Your Detox Score</p>
              </div>
              <div className="flex-1 px-5 py-4 space-y-3">
                <div className="mx-auto w-24 h-24 rounded-full bg-gradient-to-br from-[#4ADE80] to-[#FF6B4A] flex items-center justify-center">
                  <span className="font-bold text-3xl text-white">85</span>
                </div>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="bg-[#4ADE80]/10 rounded-lg p-2"><Droplets className="w-4 h-4 text-[#FF6B4A] mx-auto" /><p className="text-[10px] text-[#8C7A76] mt-1">Hydration</p><p className="text-xs font-bold text-[#1A1514]">90%</p></div>
                  <div className="bg-[#A388EE]/10 rounded-lg p-2"><Heart className="w-4 h-4 text-[#A388EE] mx-auto" /><p className="text-[10px] text-[#8C7A76] mt-1">Gut</p><p className="text-xs font-bold text-[#1A1514]">80%</p></div>
                  <div className="bg-[#4ADE80]/10 rounded-lg p-2"><Zap className="w-4 h-4 text-[#FF6B4A] mx-auto" /><p className="text-[10px] text-[#8C7A76] mt-1">Inflam.</p><p className="text-xs font-bold text-[#1A1514]">85%</p></div>
                </div>
                <div className="bg-[#4ADE80]/10 rounded-xl p-3"><p className="text-xs font-semibold text-[#FF6B4A]">7-Day Streak</p><p className="text-[10px] text-[#8C7A76] mt-1">Keep it up! You're glowing.</p></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const FooterCta = ({ onLogin }) => (
  <div className="text-center py-20 px-6 bg-gradient-to-b from-white to-[#FFF2EC]">
    <div className="max-w-xl mx-auto">
      <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#1A1514] mb-4">Your clearer skin journey starts today</h2>
      <p className="text-[#8C7A76] text-lg mb-8">Join thousands who've discovered what their skin has been trying to tell them.</p>
      <Button onClick={onLogin} data-testid="footer-cta-btn" className="bg-[#FF6B4A] hover:bg-[#E8552F] text-white rounded-full px-10 py-6 text-lg font-semibold transition-all hover:-translate-y-1">
        Start Your Journey — It's Free <ArrowRight className="ml-2 w-5 h-5" />
      </Button>
      <p className="text-xs text-[#8C7A76] mt-4 flex items-center justify-center gap-1"><Shield className="w-3 h-3" /> No credit card required. Free forever plan available.</p>
    </div>
  </div>
);

const Footer = () => (
  <footer className="bg-[#1A1514] py-10 px-6">
    <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
      <div className="flex items-center gap-2">
        <Leaf className="w-5 h-5 text-[#4ADE80]" />
        <span className="text-white font-heading font-bold">InnerGlow</span>
      </div>
      <p className="text-white/40 text-sm">&copy; {new Date().getFullYear()} InnerGlow. Your inner journey to outer radiance.</p>
    </div>
  </footer>
);

const LandingPage = () => {
  const [authOpen, setAuthOpen] = useState(false);
  const [scanModalOpen, setScanModalOpen] = useState(false);
  const [scanLoading, setScanLoading] = useState(false);
  const openAuth = () => setAuthOpen(true);
  const track = (event) => axios.post(`${API}/track`, { event }).catch(() => {});
  useEffect(() => {
    if (!sessionStorage.getItem('ig_pv')) {
      sessionStorage.setItem('ig_pv', '1');
      track('page_view');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const scrollToFeatures = () => {
    document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' });
  };
  const scrollToDemo = () => {
    document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' });
  };
  const handleInstantScan = () => {
    track('scan_cta_click');
    setScanModalOpen(true);
  };
  const handleConfirmScan = async () => {
    setScanLoading(true);
    try {
      const res = await axios.post(`${API}/scan/checkout`, { origin_url: window.location.origin });
      track('checkout_redirect');
      window.location.href = res.data.url;
    } catch (e) {
      toast.error('Could not start checkout. Please try again.');
      setScanLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FFF2EC]" data-testid="landing-page">
      <HeroSection onLogin={openAuth} onLearnMore={scrollToFeatures} onInstantScan={handleInstantScan} scanLoading={scanLoading} onWatchDemo={scrollToDemo} />
      <HowItWorksDemo onCta={handleInstantScan} />
      <FeaturesGrid />
      <AICoachPreview onLogin={openAuth} />
      <DetoxPreview />
      <SocialProof />
      <PricingComparison onLogin={openAuth} />
      <GetTheApp onLogin={openAuth} />
      <FAQSection />
      <EmailCapture />
      <FooterCta onLogin={openAuth} />
      <Footer />
      <AuthModal open={authOpen} onOpenChange={setAuthOpen} />
      <ScanPreviewModal open={scanModalOpen} onOpenChange={setScanModalOpen} onConfirm={handleConfirmScan} loading={scanLoading} />
    </div>
  );
};

export default LandingPage;
